import { type QueryFunction, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import axiosInstance from '../../../constants/axiosInstance';
import { useIntersectionObserver } from '../../../utilities/customHooks/useIntersectionObserver';
import { BatchConfigRow, BatchConfigDataType } from '../types/BatchConfigType';

export interface TemplateListApiResponse {
  data: BatchConfigDataType;
  message: string;
}

interface FetchResponse extends BatchConfigDataType {
  message: string;
}

const url = `/batch/getBatchList`;

const fetchBatchConfigurations: QueryFunction<
  FetchResponse,
  [string, { searchString: string; assetId: string }],
  number
> = async ({
  pageParam = 1,
  queryKey,
}) => {
  const [_key, { searchString, assetId }] = queryKey as [string, { searchString: string; assetId: string }];

  const { data } = await axiosInstance().post<TemplateListApiResponse>(
    url,
    {
      page: pageParam,
      name: searchString,
      assetId,
      limit: 8,
    }
  );

  return {
    ...data.data,
    message: data.message,
    currentPage: pageParam,
  };
};

const nextPageParam = (lastPage: FetchResponse, allPages: FetchResponse[]): number | undefined => {
  return lastPage.currentPage < lastPage.totalPages ? lastPage.currentPage + 1 : undefined;
};

export const useBatchConfigTable = (searchString: string, assetId: string) => {
  const lastElementRef = useRef<HTMLTableRowElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const query = useSuspenseInfiniteQuery({
    queryKey: ['batchConfigs', { searchString, assetId }] as [
      string,
      { searchString: string; assetId: string }
    ],
    queryFn: fetchBatchConfigurations,
    getNextPageParam: nextPageParam,
    initialPageParam: 1,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data, error, isError, isFetchingNextPage, hasNextPage, fetchNextPage } = query;
  const batches: BatchConfigRow[] = useMemo(() => {
    return data?.pages.flatMap(page => page?.batches) ?? [];
  }, [data]);

  useIntersectionObserver(
    tableContainerRef,
    lastElementRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  );

  return {
    batches,
    lastElementRef,
    tableContainerRef,
    error,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  };
};
